import React, { useState } from 'react';
import {
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  CircularProgress,
} from '@mui/material';
import { AppDialog } from '@/components/ui';
import type { Employee } from '../../types/employee';

export type DeleteRetiredOption = 'keepRecords' | 'deleteAll';

interface DeleteRetiredEmployeeDialogProps {
  open: boolean;
  employee: Employee | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (deleteWorkRecords: boolean) => Promise<void>;
}

const DeleteRetiredEmployeeDialog: React.FC<DeleteRetiredEmployeeDialogProps> = ({
  open,
  employee,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const [option, setOption] = useState<DeleteRetiredOption>('keepRecords');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (submitting || loading) return;
    setOption('keepRecords');
    onClose();
  };

  const handleConfirm = async () => {
    if (!employee || submitting || loading) return;
    setSubmitting(true);
    try {
      await onConfirm(option === 'deleteAll');
      setOption('keepRecords');
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || loading;

  return (
    <AppDialog
      open={open}
      title="퇴직자 정보 삭제"
      onClose={handleClose}
      maxWidth="sm"
      actions={
        <>
          <Button variant="text" onClick={handleClose} disabled={busy}>
            취소
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={busy || !employee}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            삭제
          </Button>
        </>
      }
    >
        <Typography variant="body1" sx={{ mb: 2, color: 'var(--fg-strong)' }}>
          <strong>{employee?.name ?? '직원'}</strong>님의 정보를 삭제합니다.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'var(--fg-secondary)' }}>
          인건비 신고 전에는 직원 정보 삭제를 권장하지 않습니다. 근무 기록 처리 방식을 선택해 주세요.
        </Typography>

        <RadioGroup
          value={option}
          onChange={(_, value) => setOption(value as DeleteRetiredOption)}
        >
          <Box
            sx={{
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              p: 1.5,
              mb: 1.5,
              bgcolor: option === 'keepRecords' ? 'var(--bg-elev-1)' : 'transparent',
            }}
          >
            <FormControlLabel
              value="keepRecords"
              control={<Radio />}
              disabled={busy}
              label={
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    근무 기록만 남기기
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--fg-secondary)' }}>
                    캘린더에 근무 이력은 유지하고, 직원 정보만 삭제합니다.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', m: 0 }}
            />
          </Box>

          <Box
            sx={{
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              p: 1.5,
              bgcolor: option === 'deleteAll' ? 'var(--bg-elev-1)' : 'transparent',
            }}
          >
            <FormControlLabel
              value="deleteAll"
              control={<Radio />}
              disabled={busy}
              label={
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    근무 기록까지 모두 삭제
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--fg-secondary)' }}>
                    해당 직원의 모든 근무 일정과 직원 정보를 함께 삭제합니다.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', m: 0 }}
            />
          </Box>
        </RadioGroup>
    </AppDialog>
  );
};

export default DeleteRetiredEmployeeDialog;
